import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Textarea } from "./ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Checkbox } from "./ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarNav,
  SidebarNavItem,
} from "./ui/sidebar"
import {
  Calendar,
  Cloud,
  Droplets,
  Home,
  Leaf,
  Settings,
  Sun,
  User,
  CheckCircle2,
  LogOut,
  Menu,
  X,
  Plus,
  Search,
  Edit,
  Trash2,
  Clock,
  TrendingUp,
  AlertTriangle,
  Heart,
  Zap,
  Filter,
  List,
  CalendarDays,
  Droplet,
  Sprout,
  Scissors,
  Shovel,
  Bell,
  Repeat,
  CheckCircle,
  Circle,
  ChevronDown,
  MoreHorizontal,
  Star,
} from "lucide-react"
import SharedHeader from './SharedHeader'
import UserDropdown from './UserDropdown'

// Sample tasks data
const sampleTasks = [
  {
    id: 1,
    title: "Palaistyti rožes",
    description: "Laistyti visas rožes priekiniame sode",
    type: "watering",
    plantName: "Mano Rožė",
    dueDate: "2025-07-25",
    dueTime: "09:00",
    priority: "high",
    status: "pending",
    recurring: "weekly",
    completed: false
  },
  {
    id: 2,
    title: "Patręšti pomidorus",
    description: "Organinių trąšų aplikacija",
    type: "fertilizing",
    plantName: "Pomidorai",
    dueDate: "2025-07-26",
    dueTime: "10:00",
    priority: "medium",
    status: "pending",
    recurring: "monthly",
    completed: false
  },
  {
    id: 3,
    title: "Nupjauti žolę",
    description: "Nupjauti žolę priekiniame sode",
    type: "pruning",
    plantName: null,
    dueDate: "2025-01-21",
    dueTime: "14:00",
    priority: "low",
    status: "overdue",
    recurring: "weekly",
    completed: false
  },
  {
    id: 4,
    title: "Persodinti orkidelę",
    description: "Persodinti orkidelę į didesnį puodelį",
    type: "repotting",
    plantName: "Orkidelė",
    dueDate: "2025-01-25",
    dueTime: "11:00",
    priority: "medium",
    status: "pending",
    recurring: "none",
    completed: false
  },
  {
    id: 5,
    title: "Laistyti kaktusus",
    description: "Laistyti kaktusus virtuvės lange",
    type: "watering",
    plantName: "Kaktusai",
    dueDate: "2025-01-22",
    dueTime: "08:00",
    priority: "high",
    status: "completed",
    recurring: "weekly",
    completed: true
  },
  {
    id: 6,
    title: "Patręšti citrusus",
    description: "Citrinų tręšimas specialiomis trąšomis",
    type: "fertilizing",
    plantName: "Citrina",
    dueDate: "2025-01-24",
    dueTime: "12:00",
    priority: "medium",
    status: "pending",
    recurring: "monthly",
    completed: false
  },
  {
    id: 7,
    title: "Nupjauti lavandos",
    description: "Nupjauti lavandos kad gražiau augtų",
    type: "pruning",
    plantName: "Lavanda",
    dueDate: "2025-01-26",
    dueTime: "15:00",
    priority: "low",
    status: "pending",
    recurring: "monthly",
    completed: false
  },
  {
    id: 8,
    title: "Laistyti daržoves",
    description: "Laistyti visas daržoves darželyje",
    type: "watering",
    plantName: "Daržovės",
    dueDate: "2025-01-23",
    dueTime: "07:00",
    priority: "high",
    status: "pending",
    recurring: "daily",
    completed: false
  }
]

// Sample user's plants for task creation
const sampleUserPlants = [
  { id: 1, name: "Mano Rožė" },
  { id: 2, name: "Pomidorai" },
  { id: 3, name: "Orkidelė" },
  { id: 4, name: "Kaktusai" },
  { id: 5, name: "Citrina" },
  { id: 6, name: "Lavanda" },
  { id: 7, name: "Daržovės" }
]

export default function Tasks() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, isAuthenticated } = useAuth()
  const [sidebarOpen, setSidebarOpen] = React.useState(true)
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false)
  
  // Task management state
  const [tasks, setTasks] = useState(sampleTasks)
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list')
  const [filter, setFilter] = useState<'all' | 'today' | 'week' | 'completed'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingTask, setEditingTask] = useState<any>(null)
  
  // New task form state
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    type: 'watering',
    plantId: '',
    dueDate: '',
    dueTime: '',
    priority: 'medium',
    recurring: 'none',
    reminder: false
  })


  // Show loading state while authentication is being checked
  if (!isAuthenticated) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-emerald-700">Kraunama...</p>
        </div>
      </div>
    )
  }

  // Error handling for translations
  const safeTranslate = (key: string, options?: any): string => {
    try {
      const result = t(key, options)
      return typeof result === 'string' ? result : key
    } catch (error) {
      console.error(`Translation error for key: ${key}`, error)
      return key
    }
  }

  const navigationItems = [
    {
      title: "Pagrindinis",
      description: "Pagrindinis skydelis",
      url: '/dashboard',
      icon: Home
    },
    {
      title: "Mano Sodas",
      description: "Jūsų augalų sąrašas",
      url: '/my-plants',
      icon: Heart
    },
    {
      title: "Augalų Katalogas",
      description: "Augalų duomenų bazė",
      url: '/plants',
      icon: Leaf
    },
    {
      title: "Užduotys",
      description: "Priežiūros užduotys",
      url: '/tasks',
      icon: CheckCircle2
    },
    {
      title: "Kalendorius",
      description: "Priežiūros kalendorius",
      url: '/calendar',
      icon: CalendarDays
    },
    {
      title: "Orai",
      description: "Oro prognozės",
      url: '/weather',
      icon: Cloud
    }
  ]

  // Task type configurations
  const taskTypes = {
    watering: { icon: Droplet, label: "Laistymas", color: "text-blue-600", bgColor: "bg-blue-100" },
    fertilizing: { icon: Sprout, label: "Tręšimas", color: "text-green-600", bgColor: "bg-green-100" },
    pruning: { icon: Scissors, label: "Genėjimas", color: "text-orange-600", bgColor: "bg-orange-100" },
    repotting: { icon: Shovel, label: "Persodinimas", color: "text-purple-600", bgColor: "bg-purple-100" },
    other: { icon: Settings, label: "Kita", color: "text-gray-600", bgColor: "bg-gray-100" }
  }

  // Priority configurations
  const priorities = {
    high: { label: "Aukšta", color: "text-red-600", bgColor: "bg-red-100" },
    medium: { label: "Vidutinė", color: "text-yellow-600", bgColor: "bg-yellow-100" },
    low: { label: "Žema", color: "text-green-600", bgColor: "bg-green-100" }
  }

  // Filter tasks based on current filter and search
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         task.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (task.plantName && task.plantName.toLowerCase().includes(searchQuery.toLowerCase()))
    
    const matchesFilter = (() => {
      const today = new Date().toISOString().split('T')[0]
      const taskDate = new Date(task.dueDate)
      const todayDate = new Date(today)
      
      switch (filter) {
        case 'today':
          return task.dueDate === today
        case 'week':
          const weekFromNow = new Date(todayDate.getTime() + 7 * 24 * 60 * 60 * 1000)
          return taskDate >= todayDate && taskDate <= weekFromNow
        case 'completed':
          return task.completed
        default:
          return true
      }
    })()
    
    return matchesSearch && matchesFilter
  })

  // Get task status
  const getTaskStatus = (task: any) => {
    if (task.completed) return { status: 'completed', label: 'Atlikta', color: 'text-green-600', bgColor: 'bg-green-100' }
    
    const today = new Date().toISOString().split('T')[0]
    const taskDate = new Date(task.dueDate)
    const todayDate = new Date(today)
    
    if (task.dueDate < today) {
      return { status: 'overdue', label: 'Vėluoja', color: 'text-red-600', bgColor: 'bg-red-100' }
    } else if (task.dueDate === today) {
      return { status: 'today', label: 'Šiandien', color: 'text-yellow-600', bgColor: 'bg-yellow-100' }
    } else {
      return { status: 'upcoming', label: 'Artėja', color: 'text-gray-600', bgColor: 'bg-gray-100' }
    }
  }

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000)
    
    if (date.toDateString() === today.toDateString()) {
      return 'Šiandien'
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Rytoj'
    } else {
      return date.toLocaleDateString('lt-LT', { 
        month: 'short', 
        day: 'numeric',
        weekday: 'short'
      })
    }
  }

  // Handle task completion
  const handleTaskComplete = (taskId: number) => {
    setTasks(tasks.map(task => 
      task.id === taskId 
        ? { ...task, completed: !task.completed, status: task.completed ? 'pending' : 'completed' }
        : task
    ))
  }

  // Handle task deletion
  const handleTaskDelete = (taskId: number) => {
    setTasks(tasks.filter(task => task.id !== taskId))
  }

  // Handle task editing
  const handleTaskEdit = (task: any) => {
    setEditingTask(task)
    setNewTask({
      title: task.title,
      description: task.description,
      type: task.type,
      plantId: task.plantName ? sampleUserPlants.find(p => p.name === task.plantName)?.id.toString() || '' : '',
      dueDate: task.dueDate,
      dueTime: task.dueTime,
      priority: task.priority,
      recurring: task.recurring,
      reminder: false
    })
    setShowCreateDialog(true)
  }

  // Handle form submission
  const handleSubmit = () => {
    if (!newTask.title || !newTask.dueDate) return

    const plant = sampleUserPlants.find(p => p.id.toString() === newTask.plantId)
    
    if (editingTask) {
      // Update existing task
      setTasks(tasks.map(task => 
        task.id === editingTask.id 
          ? {
              ...task,
              title: newTask.title,
              description: newTask.description,
              type: newTask.type,
              plantName: plant?.name || null,
              dueDate: newTask.dueDate,
              dueTime: newTask.dueTime,
              priority: newTask.priority,
              recurring: newTask.recurring
            }
          : task
      ))
    } else {
      // Create new task
      const newTaskObj = {
        id: Math.max(...tasks.map(t => t.id)) + 1,
        title: newTask.title,
        description: newTask.description,
        type: newTask.type,
        plantName: plant?.name || null,
        dueDate: newTask.dueDate,
        dueTime: newTask.dueTime,
        priority: newTask.priority,
        status: 'pending',
        recurring: newTask.recurring,
        completed: false
      }
      setTasks([...tasks, newTaskObj])
    }

    // Reset form
    setNewTask({
      title: '',
      description: '',
      type: 'watering',
      plantId: '',
      dueDate: '',
      dueTime: '',
      priority: 'medium',
      recurring: 'none',
      reminder: false
    })
    setEditingTask(null)
    setShowCreateDialog(false)
  }

  // Reset form when dialog closes
  const handleDialogClose = () => {
    setShowCreateDialog(false)
    setEditingTask(null)
    setNewTask({
      title: '',
      description: '',
      type: 'watering',
      plantId: '',
      dueDate: '',
      dueTime: '',
      priority: 'medium',
      recurring: 'none',
      reminder: false
    })
  }



  const handleNavigation = (url: string) => {
    navigate(url)
  }



  const isActiveRoute = (url: string) => {
    return location.pathname === url
  }

  const TaskCard = ({ task }: { task: any }) => {
    const taskType = taskTypes[task.type as keyof typeof taskTypes]
    const priority = priorities[task.priority as keyof typeof priorities]
    const status = getTaskStatus(task)
    const TaskTypeIcon = taskType.icon

    return (
      <Card className={`border-0 shadow-2xl hover:shadow-3xl transition-shadow duration-200 bg-white/80 backdrop-blur-sm ${task.completed ? 'opacity-75' : ''}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              <div className={`p-2 rounded-lg ${taskType.bgColor}`}>
                <TaskTypeIcon className={`h-5 w-5 ${taskType.color}`} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <h3 className={`font-medium ${task.completed ? 'line-through text-gray-500' : ''}`}>
                    {task.title}
                  </h3>
                  <Badge variant="outline" className={`text-xs ${priority.bgColor} ${priority.color}`}>
                    {priority.label}
                  </Badge>
                  <Badge variant="outline" className={`text-xs ${status.bgColor} ${status.color}`}>
                    {status.label}
                  </Badge>
                </div>
                
                <p className={`text-sm text-gray-600 mb-2 ${task.completed ? 'line-through' : ''}`}>
                  {task.description}
                </p>
                
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <div className="flex items-center space-x-1">
                    <Clock className="h-3 w-3" />
                    <span>{formatDate(task.dueDate)} {task.dueTime}</span>
                  </div>
                  
                  {task.plantName && (
                    <div className="flex items-center space-x-1">
                      <Leaf className="h-3 w-3" />
                      <span>{task.plantName}</span>
                    </div>
                  )}
                  
                  {task.recurring !== 'none' && (
                    <div className="flex items-center space-x-1">
                      <Repeat className="h-3 w-3" />
                      <span>{task.recurring === 'daily' ? 'Kasdien' : task.recurring === 'weekly' ? 'Kas savaitę' : 'Kas mėnesį'}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                checked={task.completed}
                onCheckedChange={() => handleTaskComplete(task.id)}
                className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
              />
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleTaskEdit(task)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Redaguoti
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => handleTaskDelete(task.id)}
                    className="text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Ištrinti
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  const EmptyState = () => (
    <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
      <CardContent className="text-center py-12">
        <CheckCircle2 className="h-16 w-16 text-emerald-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-emerald-800 mb-2">
          {filter === 'completed' ? 'Neturite atliktų užduočių' : 'Neturite užduočių'}
        </h3>
        <p className="text-emerald-600 mb-6">
          {filter === 'completed' 
            ? 'Atlikite pirmą užduotį, kad čia matytumėte savo progresą'
            : 'Sukurkite pirmą užduotį, kad pradėtumėte planuoti savo augalų priežiūrą'
          }
        </p>
        {filter !== 'completed' && (
          <Button onClick={() => setShowCreateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-2" />
            Sukurti užduotį
          </Button>
        )}
      </CardContent>
    </Card>
  )

  return (
    <div className="flex h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 tasks-page">
      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileSidebarOpen(false)} />
          <div className="fixed left-0 top-0 h-full w-64 bg-white/80 backdrop-blur-sm shadow-2xl">
            <div className="flex h-full flex-col">
              {/* Mobile Header */}
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-b border-emerald-100 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-md">
                      <Leaf className="h-5 w-5" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-lg font-bold text-emerald-800">Kiemo Draugas</span>
                      <span className="text-xs text-emerald-600">Augalų valdymas</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-emerald-700 hover:bg-emerald-100"
                    onClick={() => setMobileSidebarOpen(false)}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Mobile Navigation */}
              <div className="flex-1 px-3 py-4">
                <SidebarNav className="space-y-1">
                  {navigationItems.map((item) => {
                    const isActive = isActiveRoute(item.url)
                    return (
                      <SidebarNavItem
                        key={item.title}
                        active={isActive}
                        className={`
                          group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer
                          ${isActive 
                            ? 'bg-emerald-50 text-emerald-700 border-l-4 border-emerald-600 shadow-sm' 
                            : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                          }
                        `}
                        onClick={() => handleNavigation(item.url)}
                      >
                        <item.icon className={`h-5 w-5 transition-colors ${isActive ? 'text-emerald-600' : 'text-gray-500 group-hover:text-emerald-600'}`} />
                        <div className="flex flex-col">
                          <span>{item.title}</span>
                          <span className="text-xs text-gray-500 font-normal">{item.description}</span>
                        </div>
                      </SidebarNavItem>
                    )
                  })}
                </SidebarNav>
              </div>

              {/* Mobile Footer */}
              <div className="bg-gradient-to-t from-emerald-50 to-transparent border-t border-emerald-100 px-3 py-4">
                <SidebarNav>
                  <SidebarNavItem 
                    className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-all duration-200 cursor-pointer"
                  >
                    <Settings className="h-5 w-5 text-gray-500 group-hover:text-emerald-600 transition-colors" />
                    <div className="flex flex-col">
                      <span>Nustatymai</span>
                      <span className="text-xs text-gray-500 font-normal">Aplikacijos nustatymai</span>
                    </div>
                  </SidebarNavItem>
                </SidebarNav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <Sidebar className={`hidden lg:flex w-64 flex-col bg-white/80 backdrop-blur-sm border-r border-emerald-100 shadow-2xl transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'}`}>
        {/* Sidebar Header */}
        <SidebarHeader className="bg-gradient-to-br from-emerald-50 to-green-50 border-b border-emerald-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-md">
              <Leaf className="h-5 w-5" />
            </div>
            {sidebarOpen && (
              <div className="flex flex-col">
                <span className="text-lg font-bold text-emerald-800">Kiemo Draugas</span>
                <span className="text-xs text-emerald-600">Augalų valdymas</span>
              </div>
            )}
          </div>
        </SidebarHeader>
          {/* Navigation Content */}
          <SidebarContent className="flex-1 bg-white/80 backdrop-blur-sm px-3 py-4">
            <SidebarNav className="space-y-1">
              {navigationItems.map((item) => {
                const isActive = isActiveRoute(item.url)
                return (
                  <SidebarNavItem
                    key={item.title}
                    active={isActive}
                    className={`
                      group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer
                      ${isActive 
                        ? 'bg-emerald-50 text-emerald-700 border-l-4 border-emerald-600 shadow-sm' 
                        : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                      }
                    `}
                    onClick={() => handleNavigation(item.url)}
                  >
                    <item.icon className={`h-5 w-5 transition-colors ${isActive ? 'text-emerald-600' : 'text-gray-500 group-hover:text-emerald-600'}`} />
                    {sidebarOpen && (
                      <div className="flex flex-col">
                        <span>{item.title}</span>
                        <span className="text-xs text-gray-500 font-normal">{item.description}</span>
                      </div>
                    )}
                  </SidebarNavItem>
                )
              })}
            </SidebarNav>
          </SidebarContent>
          
          {/* Sidebar Footer */}
          <SidebarFooter className="bg-gradient-to-t from-emerald-50 to-transparent border-t border-emerald-100 px-3 py-4">
            <SidebarNav>
              <SidebarNavItem 
                className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-all duration-200 cursor-pointer"
              >
                <Settings className="h-5 w-5 text-gray-500 group-hover:text-emerald-600 transition-colors" />
                {sidebarOpen && (
                  <div className="flex flex-col">
                    <span>Nustatymai</span>
                    <span className="text-xs text-gray-500 font-normal">Aplikacijos nustatymai</span>
                  </div>
                )}
              </SidebarNavItem>
            </SidebarNav>
          </SidebarFooter>
        </Sidebar>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-emerald-100 bg-white/80 backdrop-blur-sm px-4 shadow-sm">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-emerald-700 hover:bg-emerald-50 lg:hidden"
              onClick={() => setMobileSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-emerald-700 hover:bg-emerald-50 hidden lg:flex"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="h-4 w-px bg-emerald-200" />
            <h1 className="text-lg font-semibold text-emerald-800">Užduotys</h1>
          </div>

          <div className="flex items-center gap-4">
            <Button onClick={() => setShowCreateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" />
              Sukurti užduotį
            </Button>
            <UserDropdown user={user} />
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 space-y-6 p-4 md:p-6 overflow-auto">
          {/* Filters and controls */}
          <div className="space-y-4">
            {/* Search and view toggle */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Ieškoti užduočių..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/80 backdrop-blur-sm border-emerald-200 focus:border-emerald-500"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={viewMode === 'list' ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'}
                >
                  <List className="h-4 w-4 mr-2" />
                  Sąrašas
                </Button>
                <Button
                  variant={viewMode === 'calendar' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('calendar')}
                  className={viewMode === 'calendar' ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'}
                >
                  <CalendarDays className="h-4 w-4 mr-2" />
                  Kalendorius
                </Button>
              </div>
            </div>

            {/* Filter chips */}
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'Visos' },
                { key: 'today', label: 'Šiandien' },
                { key: 'week', label: 'Šią savaitę' },
                { key: 'completed', label: 'Atliktos' }
              ].map((filterOption) => (
                <Button
                  key={filterOption.key}
                  variant={filter === filterOption.key ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter(filterOption.key as any)}
                  className={filter === filterOption.key ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'}
                >
                  {filterOption.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Tasks content */}
          {viewMode === 'list' ? (
            <div className="space-y-4">
              {filteredTasks.length > 0 ? (
                filteredTasks.map((task) => (
                  <TaskCard key={task.id} task={task} />
                ))
              ) : (
                <EmptyState />
              )}
            </div>
          ) : (
            <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
              <CardContent className="text-center py-12">
                <CalendarDays className="h-16 w-16 text-emerald-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-emerald-800 mb-2">Kalendoriaus vaizdas</h3>
                <p className="text-emerald-600">Kalendoriaus funkcija bus pridėta netolimoje ateityje</p>
              </CardContent>
            </Card>
          )}
        </main>
      </div>

      {/* Create/Edit Task Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingTask ? 'Redaguoti užduotį' : 'Sukurti naują užduotį'}
            </DialogTitle>
            <DialogDescription>
              Užpildykite informaciją apie užduotį
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Pavadinimas *</Label>
              <Input
                id="title"
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="Įveskite užduoties pavadinimą"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Aprašymas</Label>
              <Textarea
                id="description"
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                placeholder="Aprašykite užduotį detaliau"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="type">Užduoties tipas</Label>
                <Select value={newTask.type} onValueChange={(value) => setNewTask({ ...newTask, type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="watering">Laistymas</SelectItem>
                    <SelectItem value="fertilizing">Tręšimas</SelectItem>
                    <SelectItem value="pruning">Genėjimas</SelectItem>
                    <SelectItem value="repotting">Persodinimas</SelectItem>
                    <SelectItem value="other">Kita</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="priority">Prioritetas</Label>
                <Select value={newTask.priority} onValueChange={(value) => setNewTask({ ...newTask, priority: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Žemas</SelectItem>
                    <SelectItem value="medium">Vidutinis</SelectItem>
                    <SelectItem value="high">Aukštas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="plant">Augalas (neprivaloma)</Label>
              <Select value={newTask.plantId} onValueChange={(value) => setNewTask({ ...newTask, plantId: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Pasirinkite augalą" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Nepasirinkta</SelectItem>
                  {sampleUserPlants.map((plant) => (
                    <SelectItem key={plant.id} value={plant.id.toString()}>
                      {plant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dueDate">Data *</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={newTask.dueDate}
                  onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                />
              </div>
              
              <div>
                <Label htmlFor="dueTime">Laikas</Label>
                <Input
                  id="dueTime"
                  type="time"
                  value={newTask.dueTime}
                  onChange={(e) => setNewTask({ ...newTask, dueTime: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="recurring">Kartojimas</Label>
              <Select value={newTask.recurring} onValueChange={(value) => setNewTask({ ...newTask, recurring: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nekartojama</SelectItem>
                  <SelectItem value="daily">Kasdien</SelectItem>
                  <SelectItem value="weekly">Kas savaitę</SelectItem>
                  <SelectItem value="monthly">Kas mėnesį</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={handleDialogClose}>
              Atšaukti
            </Button>
            <Button onClick={handleSubmit} disabled={!newTask.title || !newTask.dueDate}>
              {editingTask ? 'Atnaujinti' : 'Sukurti'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
} 